import React, { useEffect, useRef, useState } from "react";
import { Badge, Button, Group, Stack, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import moment from "moment";
import { Page } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import ApprovalFundListFunds from "../../components/approvalFundListFunds";

const formatDate = (value) =>
  value ? moment(value).format("YYYY-MM-DD") : "";

const formatEffectivePeriod = (list) => {
  const from = list?.effectiveFrom;
  const to = list?.effectiveTo;
  if (!from && !to) return "--";
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
  return formatDate(from || to);
};

const ConsultantApprovalFundList = () => {
  const [api] = useServerApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [loading, setLoading] = useState(true);
  const [acknowledging, setAcknowledging] = useState(false);
  const [list, setList] = useState(null);
  const [funds, setFunds] = useState([]);
  const [acknowledgement, setAcknowledgement] = useState({
    acknowledged: false,
    passedAt: null,
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const data = await apiRef.current.ApprovalFundList.getMyCurrent();
        if (cancelled) return;
        setList(data?.approvalFundList ?? null);
        setFunds(data?.funds ?? []);
        setAcknowledgement(
          data?.acknowledgement ?? { acknowledged: false, passedAt: null }
        );
      } catch (error) {
        if (cancelled) return;
        setList(null);
        setFunds([]);
        setAcknowledgement({ acknowledged: false, passedAt: null });
        showNotification({
          color: "red",
          title: "Approved Fund List",
          message: error?.message || "Failed to load approved fund list",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const onAcknowledge = async () => {
    setAcknowledging(true);
    try {
      const data = await api.ApprovalFundList.acknowledge();
      setAcknowledgement(
        data?.acknowledgement ?? { acknowledged: true, passedAt: new Date() }
      );
      showNotification({
        title: "Acknowledged",
        message: "Your acknowledgement has been recorded.",
      });
    } catch (error) {
      showNotification({
        color: "red",
        title: "Approved Fund List",
        message: error?.message || "Failed to acknowledge fund list",
      });
    } finally {
      setAcknowledging(false);
    }
  };

  const acknowledged = acknowledgement?.acknowledged === true;

  return (
    <Page title="Approved Fund List">
      {loading && <Text color="dimmed">Loading...</Text>}
      {!loading && !list && (
        <Text color="dimmed">No effective approved fund list is available.</Text>
      )}
      {!loading && list && (
        <Stack spacing="lg">
          <Stack spacing={4}>
            <Text weight={600}>{list.name || list.code}</Text>
            <Text size="sm" color="dimmed">
              {list.code ? `Code: ${list.code}` : "Code: --"}
            </Text>
            <Text size="sm" color="dimmed">
              Effective period: {formatEffectivePeriod(list)}
            </Text>
          </Stack>

          {acknowledged && (
            <Group spacing="xs">
              <Badge color="green">Acknowledged</Badge>
              <Text size="sm" color="dimmed">
                {acknowledgement.passedAt
                  ? moment(acknowledgement.passedAt).format("YYYY-MM-DD HH:mm")
                  : ""}
              </Text>
            </Group>
          )}

          {!acknowledged && (
            <Group>
              <Button loading={acknowledging} onClick={onAcknowledge}>
                Acknowledge
              </Button>
            </Group>
          )}

          <ApprovalFundListFunds readOnly funds={funds} />
        </Stack>
      )}
    </Page>
  );
};

export default ConsultantApprovalFundList;
